/**
 * Simplified Chinese message catalog — mirrors the shape of
 * `messages.en.ts` exactly; `satisfies MessagesShape` makes a missing or
 * renamed key a TypeScript error. Register: 你 (no T–V distinction
 * locked in l10n/locales.md; modern app convention, never 您).
 *
 * Platform terms follow zh-CN UI conventions (设置, 配对 for pairing,
 * 镜像 for mirroring — Android-zh-CN's own screen-mirroring term,
 * 通知使用权 for notification access, 清除 for dismiss). "Glance" is
 * the untranslated feature brand name (glossary D1); "剪贴项" renders
 * the D2 clip noun (proposed zh reading — see the session report).
 *
 * Plural notes: Chinese is an `other`-only CLDR locale — measure words
 * (条/台/个) carry the number; every `selectPlural("zh-CN", …)`
 * supplies only an `other` form.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "已连接",
    statusReconnecting: "正在重新连接…",
    statusDisconnected: "已断开",
    openSettings: "打开设置",
    pairedFallbackDevice: "你的手机",
    publicModeToggleEnable: "隐藏通知内容",
    publicModeToggleDisable: "显示通知内容",
  },

  notifications: {
    replyAriaLabel: "回复",
    replyPlaceholder: "回复…",
    replySend: "发送",
    sent: "已发送",
    failedToSend: "发送失败",
    unread: "未读",
    dismiss: "清除",
    notificationImageAlt: "通知图片",
  },

  conversations: {
    replyAriaLabel: "回复",
    replyPlaceholder: "回复…",
    replySend: "发送",
    sent: "已发送",
    failedToSend: "发送失败",
    dismiss: "清除对话",
    messagesListLabel: "此对话中的消息",
    deviceOwner: "我",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "没有未读消息"
        : `${count} ${selectPlural("zh-CN", count, { other: "条未读消息" })}`,
  },

  list: {
    today: "今天",
    yesterday: "昨天",
    searchNotifications: "搜索通知",
    clearSearch: "清除搜索",
    clearAll: "全部清除",
    markAllRead: "全部标为已读",
    groupCheckboxLabel: "分组显示",
    noNotificationsTitle: "还没有通知",
    noNotificationsDesc: "从手机镜像过来的新消息会显示在这里。",
    noMatchesTitle: "没有匹配结果",
    notificationsList: "通知",
    privacyAppList: "按应用查看通知",
    clear: "清除",
    clearMatches: (count: number): string =>
      `清除 ${count} ${selectPlural("zh-CN", count, { other: "条匹配结果" })}`,
    noMatchesDesc: (query: string): string => `没有与“${query}”匹配的通知。`,
    appNotificationsGroup: (appName: string): string => `${appName} 的通知`,
    dismissAllFrom: (appName: string): string => `清除 ${appName} 的全部通知`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("zh-CN", count, { other: "条消息" })
          : kind === "calls"
            ? selectPlural("zh-CN", count, { other: "个来电" })
            : selectPlural("zh-CN", count, { other: "条通知" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "清除",
    kbdNavigate: "切换",
    kbdOrSeparator: "或",
  },

  settings: {
    back: "返回通知列表",
    settings: "设置",
    appearance: "外观",
    theme: "主题",
    themeAuto: "自动",
    themeLight: "浅色",
    themeDark: "深色",
    language: "语言",
    languageSystemDefault: "系统默认",
    notificationsSection: "通知",
    osNotifications: "系统通知",
    callNotifications: "来电通知",
    privacySection: "隐私",
    analyticsToggle: "匿名使用统计",
    analyticsToggleDescription: "仅计数和里程碑——绝不包含你的通知。",
    connection: "连接",
    status: "状态",
    statusConnected: "已连接",
    statusReconnecting: "正在重新连接…",
    statusDisconnected: "已断开",
    disconnectFromPhone: "与手机断开连接",
    disconnectConfirm: "确定要断开吗？设备将解除配对。",
    yesDisconnect: "确定断开",
    cancel: "取消",
    aboutAndSupport: "关于与支持",
    version: "版本",
    encryption: "加密",
    encryptionValue: "E2E (NaCl)",
    needHelp: "需要帮助？发邮件到 __EMAIL__",
    disconnectFailed: "断开失败——请重试",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `已断开，但本地清理失败：${errorMsg}。请刷新页面。`,
    disconnectLocalCleanupFailed: "已断开，但本地清理失败。请刷新页面。",
  },

  pairing: {
    enterCodeInstruction: "在 Reflecto 应用中输入此配对码",
    pairedSuccessfully: "配对成功！",
    pairingFailed: "配对失败",
    requestingPairingCode: "正在请求配对码…",
    codeExpired: "配对码已过期",
    tryAgain: "再试一次",
    failedToStartPairing: "无法开始配对",
    needAppText: "需要 Android 应用？发邮件到 __EMAIL__ 获取访问权限。",
  },

  dnd: {
    title: "勿扰",
    description: "在接下来的 1 小时内暂停此设备上的通知。",
    snooze1h: "暂停 1 小时",
    snoozeActiveUntil: (formattedTime: string): string => `生效至 ${formattedTime}`,
    cancelSnooze: "取消",
  },

  connectors: {
    title: "已连接的服务",
    emptyBody:
      "还没有已连接的服务。在 ChatGPT 或 Claude 的连接器设置中发起连接，然后在手机上批准配对码。",
    scopeSend: "可以发送通知",
    added: (date: string): string => `添加于 ${date}`,
    lastUsed: (relative: string): string => `上次使用 ${relative}`,
    neverUsed: "从未使用",
    revoke: "断开",
    revokeConfirm: (label: string): string => `要断开 ${label} 吗？它会立即失去访问权限。`,
    revokeAction: "断开",
    revokeConfirmLabel: (label: string): string => `断开 ${label}`,
    cancel: "取消",
    loadError: "无法加载已连接的服务。请重试。",
    revokeError: "无法断开该服务。请重试。",
    retry: "重试",
  },

  feedback: {
    title: "发送反馈",
    close: "关闭",
    messageLabel: "反馈内容",
    messagePlaceholder: "发现了 Bug、有想法，或者觉得哪里不对劲？告诉我们。",
    categoryLabel: "类别",
    categoryBug: "Bug",
    categoryIdea: "想法",
    categoryOther: "其他",
    emailLabel: "电子邮件（选填，仅在需要回复时填写）",
    emailPlaceholder: "you@example.com",
    emailHelp: "我们不会把你加入任何邮件列表。",
    disclosure:
      "你的反馈会发送到 Reflecto 服务器，并记录在我们的内部问题跟踪系统中。为了关联报告，我们会附上应用版本和配对时分配的设备 ID。我们看不到你的通知、联系人或任何个人数据。电子邮件为选填——仅在需要回复时填写。我们不会把你加入任何邮件列表。",
    submit: "发送反馈",
    sending: "正在发送…",
    success: "谢谢——会有真人阅读，通常在 24 小时内",
    error: "发送失败。请重试。",
    done: "完成",
    sendFeedback: "发送反馈",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "关闭",
    aiSummaryTag: "AI 摘要",
    errorAi: "现在无法生成摘要。",
    retryCta: "再试一次",
    downloadHeading: "获取 AI 摘要",
    downloadBody: "用设备端 AI 摘要快速了解错过的通知。全部在你的设备上运行。",
    downloadCta: "下载模型",
    downloadPrivacyNote: "只需下载一次。服务器永远看不到你的通知。",
    downloadingHeading: "正在下载模型…",
    caughtUp: "都看完了——现在没有需要你处理的事。",
    needsYou: "需要处理",
    quiet: "其他",
    scanning: (pending: number): string => `正在摘要 ${pending} 条…`,
    jump: "打开通知",
    affordanceReply: "回复",
    affordanceOpen: "打开",
    affordanceCopyOtp: "复制验证码",
    buttonLabel: "Glance",
  },

  update: {
    title: "有可用更新",
    description: "新版 Reflecto 已就绪。刷新即可更新。",
    reload: "刷新",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `我们将这台设备命名为“${name}”。`,
    cardLooksGood: "可以",
    cardEdit: "修改",
    cardAriaLabel: "确认设备名称",
    renameTitle: "重命名这台设备",
    renameLabel: "设备名称",
    renameSave: "保存",
    renameCancel: "取消",
    renameFailed: "重命名失败。请重试。",
  },

  collision: {
    title: (name: string): string => `你已经有一台“${name}”`,
    subtitle: "这台设备叫什么好？",
    chipWork: (name: string): string => `工作 ${name}`,
    chipHome: (name: string): string => `家用 ${name}`,
    chipPersonal: (name: string): string => `个人 ${name}`,
    customLabel: "自定义名称",
    save: "保存",
    useAnyway: "仍使用原来的名称",
    saveFailed: "新名称保存失败。请重试。",
    ariaLabel: "解决重复的设备名称",
  },

  clipboard: {
    title: "剪贴板",
    empty: "其他设备上的剪贴项会显示在这里。点按即可复制。",
    from: (label: string): string => `来自 ${label}`,
    copy: "复制到剪贴板",
    copied: "已复制",
    reveal: "显示",
    hide: "隐藏",
    sensitiveChip: "敏感内容",
    cloudChip: "来自你的 API 令牌",
    maskedHint: "内容已隐藏——点按“显示”查看",
    sendPlaceholder: "粘贴要发送的文本…",
    sendCta: "发送",
    sendSensitiveToggle: "标记为敏感内容",
    sendSuccess: (n: number): string =>
      `已发送到 ${n} ${selectPlural("zh-CN", n, { other: "台设备" })}。`,
    sendPartial: (n: number, total: number): string =>
      `已发送到 ${total} ${selectPlural("zh-CN", total, { other: "台设备" })}中的 ${n} 台。`,
    sendNoDevices: "没有可接收的已配对设备。",
    sendFailed: "发送失败。请重试。",
    sendEmpty: "没有可发送的内容。",
    dockExpand: "显示剪贴记录",
    dockCollapse: "收起",
    dockSendTitle: "发送到我的设备",
    dockFromClipboard: (chars: number): string =>
      `来自剪贴板 · ${chars} ${selectPlural("zh-CN", chars, { other: "个字符" })}`,
    dockSend: (n: number): string => `发送到 ${n} 台设备`,
    dockCancel: "取消",
    dockRecentHeading: "最近",
    dockEmptyClipboard: "剪贴板是空的。",
    shareTitle: "发送到我的设备",
    shareReview: "确认要发送的内容。它会经端到端加密发送到你的已配对设备。",
    shareSend: "发送",
    shareCancel: "取消",
    shareNotPaired: "要发送分享的文本，请先配对一台设备。",
    shareOpenApp: "打开 Reflecto",
  },
  install: {
    title: "安装 Reflecto",
    body: "添加到主屏幕，访问更快捷。",
    cta: "安装",
    dismissAria: "关闭安装提示",
  },
  common: {
    loading: "正在加载",
  },
  errors: {
    notPaired: "未配对",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${os} 上的 ${browser}`,
    webFallback: "网页浏览器",
  },
  palette: {
    placeholder: "输入命令…",
    noResults: "没有匹配的命令",
    ariaLabel: "命令面板",
    clearAll: "清除所有通知",
    glance: "Glance 摘要",
    cancelSnooze: "取消暂停",
    copyClip: "复制最近收到的剪贴项",
  },
} satisfies MessagesShape;
